import React from 'react';

export interface SaveStep {
  id: string;
  label: string;
  status: 'pending' | 'in-progress' | 'success' | 'warning' | 'error';
  error?: string;
  details?: string;
}

interface SaveProgressModalProps {
  isOpen: boolean;
  steps: SaveStep[];
  onClose?: () => void; // Currently not used - close button removed per requirements
}

export const SaveProgressModal: React.FC<SaveProgressModalProps> = ({ 
  isOpen, 
  steps,
  onClose: _ // Currently not used - close button removed per requirements
}) => {
  if (!isOpen) return null;
  
  const allComplete = steps.every(s => s.status === 'success' || s.status === 'warning' || s.status === 'error');
  const hasErrors = steps.some(s => s.status === 'error');
  const hasWarnings = steps.some(s => s.status === 'warning');

  const getIcon = (status: SaveStep['status']) => {
    switch (status) {
      case 'pending':
        return '○';
      case 'in-progress':
        return '⟳';
      case 'success':
        return '✓';
      case 'warning':
        return '⚠';
      case 'error':
        return '✗';
    }
  };

  const getStatusColor = (status: SaveStep['status']) => {
    switch (status) {
      case 'pending':
        return 'text-gray-400';
      case 'in-progress':
        return 'text-blue-500';
      case 'success':
        return 'text-green-500';
      case 'warning':
        return 'text-yellow-500';
      case 'error':
        return 'text-red-500';
    }
  };

  const getBackgroundColor = (status: SaveStep['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-50';
      case 'in-progress':
        return 'bg-blue-50';
      case 'success':
        return 'bg-green-50';
      case 'warning':
        return 'bg-yellow-50';
      case 'error':
        return 'bg-red-50';
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-save-progress">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold mb-4">
          {allComplete ? (hasErrors ? 'Save Completed with Errors' : hasWarnings ? 'Save Completed with Warnings' : 'Save Complete') : 'Saving Changes...'}
        </h2>
        
        <div className="space-y-2 mb-4">
          {steps.map((step) => (
            <div 
              key={step.id}
              className={`flex items-start p-3 rounded-lg transition-all ${getBackgroundColor(step.status)}`}
            >
              <span 
                className={`mr-3 text-lg font-bold ${getStatusColor(step.status)} ${
                  step.status === 'in-progress' ? 'animate-spin' : ''
                }`}
              >
                {getIcon(step.status)}
              </span>
              
              <div className="flex-1">
                <div className={`font-medium ${step.status === 'error' ? 'text-red-700' : step.status === 'warning' ? 'text-yellow-700' : ''}`}>
                  {step.label}
                </div>

                {step.error && (
                  <div className="text-sm text-red-600 mt-1">
                    {step.error}
                  </div>
                )}

                {step.details && !step.error && (
                  <div className={`text-sm mt-1 ${step.status === 'warning' ? 'text-yellow-600' : 'text-gray-600'}`}>
                    {step.details}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {steps.filter(s => s.status === 'success' || s.status === 'warning').length} of {steps.length} completed
          </div>
          
        </div>
        
        {!allComplete && (
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all"
                style={{
                  width: `${(steps.filter(s => s.status === 'success' || s.status === 'warning' || s.status === 'error').length / steps.length) * 100}%`
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};